import {
  forceSetPresentationPlanIdx,
  getPeakPresentationPlanIdx,
  healStalePresentationAuthorityIdx
} from '@/helpers/playbackPresentationSession'
import { setPlayerDiagnosticFaultActive } from '@/helpers/playerDiagnostics/playerDiagnosticService'
import {
  reportStalePresentationPeakHealToSentry,
  type TStalePresentationPeakHealReport
} from '@/helpers/reportStalePresentationPeakHealToSentry'

export type TStalePresentationPeakHealResult = TStalePresentationPeakHealReport & {
  healed: true
}

/**
 * Self-heal when monotonic presentation peak outlives the current plan (#1662).
 * Caps/force-sets authority, arms always-on fault `stale_presentation_peak` for Admin/ops,
 * and reports a dedicated Sentry issue for investigation.
 *
 * Returns null when peak is already in-range (and clears a prior fault episode).
 */
export function healStalePresentationPeakAuthority(args: {
  planLen: number
  fallbackIdx?: number
  /**
   * Resolve the frame id for the healed index. Prefer this over a precomputed `frameId` so a
   * missing visible fallback (`-1` → last slot) cannot pair last-slot index with head frame id.
   */
  frameIdForHealedIdx?: (afterIdx: number) => string | null | undefined
  /** Used only when {@link frameIdForHealedIdx} is omitted and fallbackIdx is in-range. */
  frameId?: string | null
  nowMs?: number
}): TStalePresentationPeakHealResult | null {
  const planLen = args.planLen
  const fallbackIdx = args.fallbackIdx ?? -1
  const nowMs = args.nowMs ?? Date.now()
  if (planLen <= 1) {
    return null
  }

  const beforePeakIdx = getPeakPresentationPlanIdx()
  if (beforePeakIdx < planLen) {
    setPlayerDiagnosticFaultActive('stale_presentation_peak', false, nowMs, 0)
    return null
  }

  const afterIdx = healStalePresentationAuthorityIdx(beforePeakIdx, planLen, fallbackIdx)
  const resolvedFromIdx = args.frameIdForHealedIdx?.(afterIdx)
  const frameId = resolvedFromIdx != null && resolvedFromIdx !== ''
    ? resolvedFromIdx
    : (fallbackIdx >= 0 && fallbackIdx < planLen
        ? (args.frameId ?? null)
        : null)
  forceSetPresentationPlanIdx(afterIdx, frameId)

  /** detailMs carries the pre-heal peak so ops can see how far out of range it was. */
  setPlayerDiagnosticFaultActive('stale_presentation_peak', true, nowMs, beforePeakIdx)

  const report: TStalePresentationPeakHealResult = {
    healed: true,
    beforePeakIdx,
    afterIdx,
    planLen,
    fallbackIdx,
    frameId
  }
  reportStalePresentationPeakHealToSentry(report)
  return report
}
